export type PermissionRisk = 'Thấp' | 'Vừa' | 'Cao' | 'Rất cao';
export type PermissionScope = 'Toàn hệ thống' | 'Công ty' | 'Chi nhánh' | 'Kho' | 'Dữ liệu của tôi';

export interface PermissionCatalogAction {
  code: string;
  name: string;
  description: string;
  risk: PermissionRisk;
  dependencies?: string[];
  conflicts?: string[];
  scopes: PermissionScope[];
}

export interface PermissionCatalogResource {
  code: string;
  name: string;
  actions: PermissionCatalogAction[];
}

export interface PermissionCatalogModule {
  code: string;
  name: string;
  description: string;
  resources: PermissionCatalogResource[];
}

export const permissionCatalog: PermissionCatalogModule[] = [
  {
    code: 'settings',
    name: 'Cài đặt hệ thống',
    description: 'Quản trị cấu hình, vai trò, người dùng và quyền truy cập.',
    resources: [
      {
        code: 'system',
        name: 'Thông tin hệ thống',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem cấu hình hệ thống.', risk: 'Thấp', scopes: ['Công ty'] },
          { code: 'update', name: 'Cập nhật', description: 'Cập nhật thông tin hệ thống và công ty.', risk: 'Cao', dependencies: ['settings.system.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'account',
        name: 'Tài khoản',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem hồ sơ tài khoản và danh sách tài khoản thanh toán.', risk: 'Thấp', scopes: ['Công ty', 'Dữ liệu của tôi'] },
          { code: 'create', name: 'Tạo', description: 'Thêm tài khoản thanh toán mới.', risk: 'Vừa', dependencies: ['settings.account.view'], scopes: ['Công ty'] },
          { code: 'update', name: 'Cập nhật', description: 'Cập nhật thông tin tài khoản.', risk: 'Vừa', dependencies: ['settings.account.view'], scopes: ['Công ty', 'Dữ liệu của tôi'] },
          { code: 'delete', name: 'Xóa', description: 'Xóa tài khoản thanh toán.', risk: 'Cao', dependencies: ['settings.account.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'notifications',
        name: 'Thông báo',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem cấu hình thông báo.', risk: 'Thấp', scopes: ['Dữ liệu của tôi'] },
          { code: 'update', name: 'Cập nhật', description: 'Cập nhật cấu hình thông báo.', risk: 'Vừa', dependencies: ['settings.notifications.view'], scopes: ['Dữ liệu của tôi'] },
        ],
      },
      {
        code: 'roles',
        name: 'Vai trò',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách và chi tiết vai trò.', risk: 'Vừa', scopes: ['Công ty'] },
          { code: 'create', name: 'Tạo', description: 'Tạo vai trò tùy chỉnh mới.', risk: 'Cao', dependencies: ['settings.roles.view'], scopes: ['Công ty'] },
          { code: 'update', name: 'Sửa', description: 'Cập nhật thông tin và quyền của vai trò.', risk: 'Rất cao', dependencies: ['settings.roles.view'], scopes: ['Công ty'] },
          { code: 'delete', name: 'Xóa', description: 'Xóa vai trò tùy chỉnh chưa có người dùng.', risk: 'Rất cao', dependencies: ['settings.roles.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'permissions',
        name: 'Phân quyền',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem ma trận phân quyền.', risk: 'Cao', scopes: ['Công ty'] },
          { code: 'manage', name: 'Quản lý', description: 'Bật tắt quyền cho các vai trò.', risk: 'Rất cao', dependencies: ['settings.permissions.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'members',
        name: 'Thành viên',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem hồ sơ thành viên.', risk: 'Thấp', scopes: ['Công ty', 'Chi nhánh'] },
          { code: 'invite', name: 'Mời', description: 'Mời thành viên vào công ty.', risk: 'Cao', dependencies: ['settings.members.view'], scopes: ['Công ty'] },
          { code: 'update', name: 'Sửa', description: 'Cập nhật hồ sơ và vai trò thành viên.', risk: 'Cao', dependencies: ['settings.members.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'categories',
        name: 'Danh mục',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem cây danh mục hệ thống.', risk: 'Thấp', scopes: ['Công ty'] },
          { code: 'manage', name: 'Quản lý', description: 'Tạo, sửa hoặc xóa danh mục hệ thống.', risk: 'Cao', dependencies: ['settings.categories.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'backup',
        name: 'Sao lưu',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách bản sao lưu.', risk: 'Cao', scopes: ['Công ty'] },
          { code: 'create', name: 'Tạo', description: 'Tạo bản sao lưu dữ liệu.', risk: 'Rất cao', dependencies: ['settings.backup.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'activity',
        name: 'Nhật ký hoạt động',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem nhật ký hoạt động hệ thống.', risk: 'Vừa', scopes: ['Công ty'] },
        ],
      },
      {
        code: 'warehouses',
        name: 'Quản lý Kho',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách và chi tiết kho.', risk: 'Thấp', scopes: ['Công ty', 'Chi nhánh'] },
          { code: 'create', name: 'Tạo', description: 'Thêm kho mới vào hệ thống.', risk: 'Vừa', dependencies: ['settings.warehouses.view'], scopes: ['Công ty'] },
          { code: 'update', name: 'Sửa', description: 'Cập nhật thông tin kho.', risk: 'Vừa', dependencies: ['settings.warehouses.view'], scopes: ['Công ty'] },
          { code: 'delete', name: 'Xóa', description: 'Xóa kho khỏi hệ thống.', risk: 'Cao', dependencies: ['settings.warehouses.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'partners',
        name: 'Đối tác / NCC',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách và hồ sơ đối tác.', risk: 'Thấp', scopes: ['Công ty'] },
          { code: 'create', name: 'Tạo', description: 'Thêm đối tác mới.', risk: 'Vừa', dependencies: ['settings.partners.view'], scopes: ['Công ty'] },
          { code: 'update', name: 'Sửa', description: 'Cập nhật thông tin đối tác.', risk: 'Vừa', dependencies: ['settings.partners.view'], scopes: ['Công ty'] },
          { code: 'delete', name: 'Xóa', description: 'Xóa đối tác.', risk: 'Cao', dependencies: ['settings.partners.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'templates',
        name: 'Mẫu tài liệu',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách mẫu tài liệu.', risk: 'Thấp', scopes: ['Công ty'] },
          { code: 'manage', name: 'Quản lý', description: 'Quản lý các mẫu tài liệu hệ thống.', risk: 'Cao', dependencies: ['settings.templates.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'integrations',
        name: 'Tích hợp',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách các tích hợp.', risk: 'Thấp', scopes: ['Công ty'] },
          { code: 'manage', name: 'Quản lý', description: 'Cấu hình và quản lý các tích hợp hệ thống.', risk: 'Cao', dependencies: ['settings.integrations.view'], scopes: ['Công ty'] },
        ],
      },
    ],
  },
  {
    code: 'inventory',
    name: 'Kho vật tư',
    description: 'Quản lý tồn kho, nhập xuất, điều chỉnh và kiểm kê.',
    resources: [
      {
        code: 'items',
        name: 'Vật tư',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh mục vật tư.', risk: 'Thấp', scopes: ['Công ty', 'Chi nhánh', 'Kho'] },
          { code: 'create', name: 'Tạo', description: 'Tạo vật tư mới.', risk: 'Vừa', dependencies: ['inventory.items.view'], scopes: ['Công ty', 'Kho'] },
          { code: 'update', name: 'Sửa', description: 'Cập nhật thông tin vật tư.', risk: 'Vừa', dependencies: ['inventory.items.view'], scopes: ['Công ty', 'Kho'] },
          { code: 'delete', name: 'Xóa', description: 'Xóa vật tư không còn sử dụng.', risk: 'Cao', dependencies: ['inventory.items.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'stock',
        name: 'Tồn kho',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem số lượng tồn kho.', risk: 'Thấp', scopes: ['Công ty', 'Chi nhánh', 'Kho'] },
          { code: 'import', name: 'Nhập', description: 'Ghi nhận nhập kho.', risk: 'Cao', dependencies: ['inventory.stock.view'], scopes: ['Kho'] },
          { code: 'export', name: 'Xuất', description: 'Ghi nhận xuất kho.', risk: 'Cao', dependencies: ['inventory.stock.view'], scopes: ['Kho'] },
          { code: 'adjust', name: 'Điều chỉnh', description: 'Điều chỉnh tồn kho thực tế.', risk: 'Rất cao', dependencies: ['inventory.stock.view'], scopes: ['Kho'] },
          { code: 'approve', name: 'Duyệt', description: 'Duyệt phiếu nhập xuất hoặc điều chỉnh.', risk: 'Rất cao', dependencies: ['inventory.stock.view'], scopes: ['Công ty', 'Chi nhánh'] },
        ],
      },
      {
        code: 'requests',
        name: 'Yêu cầu vật tư',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách yêu cầu vật tư.', risk: 'Thấp', scopes: ['Công ty', 'Chi nhánh', 'Dữ liệu của tôi'] },
          { code: 'create', name: 'Tạo', description: 'Tạo yêu cầu vật tư mới.', risk: 'Vừa', dependencies: ['inventory.requests.view'], scopes: ['Công ty', 'Chi nhánh'] },
          { code: 'update', name: 'Sửa', description: 'Cập nhật yêu cầu vật tư.', risk: 'Vừa', dependencies: ['inventory.requests.view'], scopes: ['Công ty', 'Chi nhánh', 'Dữ liệu của tôi'] },
          { code: 'approve', name: 'Duyệt', description: 'Phê duyệt yêu cầu vật tư.', risk: 'Cao', dependencies: ['inventory.requests.view'], scopes: ['Công ty', 'Chi nhánh'] },
        ],
      },
    ],
  },
  {
    code: 'estimates',
    name: 'Dự toán',
    description: 'Quản lý dự toán, định mức, quy trình mẫu và đơn giá dự toán.',
    resources: [
      {
        code: 'general',
        name: 'Dự toán',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách và chi tiết dự toán.', risk: 'Thấp', scopes: ['Công ty'] },
          { code: 'create', name: 'Tạo', description: 'Tạo dự toán và hạng mục dự toán.', risk: 'Vừa', dependencies: ['estimates.view'], scopes: ['Công ty'] },
          { code: 'update', name: 'Sửa', description: 'Cập nhật dự toán ở trạng thái được phép sửa.', risk: 'Cao', dependencies: ['estimates.view'], scopes: ['Công ty'] },
          { code: 'delete', name: 'Xóa', description: 'Xóa dữ liệu dự toán chưa khóa.', risk: 'Rất cao', dependencies: ['estimates.view'], scopes: ['Công ty'] },
          { code: 'approve', name: 'Duyệt', description: 'Duyệt dự toán trước khi khóa hoặc chuyển BOQ.', risk: 'Cao', dependencies: ['estimates.view'], scopes: ['Công ty'] },
          { code: 'export', name: 'Xuất', description: 'Xuất dữ liệu dự toán.', risk: 'Vừa', dependencies: ['estimates.view'], scopes: ['Công ty'] },
          { code: 'apply_process', name: 'Áp quy trình', description: 'Áp quy trình mẫu vào hạng mục dự toán.', risk: 'Cao', dependencies: ['estimates.update', 'estimates.process_templates.view'], scopes: ['Công ty'] },
          { code: 'view_cost', name: 'Xem chi phí', description: 'Xem chi phí và đơn giá nhạy cảm.', risk: 'Cao', dependencies: ['estimates.view'], scopes: ['Công ty'] },
          { code: 'override_price', name: 'Ghi đè đơn giá', description: 'Ghi đè đơn giá tài nguyên dự toán có lý do.', risk: 'Rất cao', dependencies: ['estimates.view_cost'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'process_templates',
        name: 'Quy trình mẫu',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem thư viện quy trình mẫu.', risk: 'Thấp', scopes: ['Công ty'] },
          { code: 'create', name: 'Tạo', description: 'Tạo quy trình mẫu mới.', risk: 'Vừa', dependencies: ['estimates.process_templates.view'], scopes: ['Công ty'] },
          { code: 'update', name: 'Sửa', description: 'Sửa quy trình mẫu, bước và tài nguyên.', risk: 'Cao', dependencies: ['estimates.process_templates.view'], scopes: ['Công ty'] },
          { code: 'delete', name: 'Xóa', description: 'Xóa hoặc ngưng dùng quy trình mẫu.', risk: 'Cao', dependencies: ['estimates.process_templates.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'norms',
        name: 'Định mức dự toán',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem thư viện định mức dự toán.', risk: 'Thấp', scopes: ['Công ty'] },
          { code: 'create', name: 'Tạo', description: 'Tạo định mức dự toán.', risk: 'Vừa', dependencies: ['estimates.norms.view'], scopes: ['Công ty'] },
          { code: 'update', name: 'Sửa', description: 'Sửa định mức dự toán.', risk: 'Cao', dependencies: ['estimates.norms.view'], scopes: ['Công ty'] },
        ],
      },
    ],
  },
  {
    code: 'equipment',
    name: 'Thiết bị & Máy móc',
    description: 'Quản lý danh mục, điều động và bảo trì thiết bị.',
    resources: [
      {
        code: 'items',
        name: 'Thiết bị',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách thiết bị.', risk: 'Thấp', scopes: ['Công ty', 'Chi nhánh'] },
          { code: 'manage', name: 'Quản lý', description: 'Thêm, sửa, xóa thông tin thiết bị.', risk: 'Cao', dependencies: ['equipment.items.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'requests',
        name: 'Yêu cầu thiết bị',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem yêu cầu điều động thiết bị.', risk: 'Thấp', scopes: ['Công ty', 'Chi nhánh', 'Dữ liệu của tôi'] },
          { code: 'create', name: 'Tạo', description: 'Tạo yêu cầu thiết bị mới.', risk: 'Vừa', dependencies: ['equipment.requests.view'], scopes: ['Công ty', 'Chi nhánh'] },
          { code: 'update', name: 'Sửa', description: 'Sửa yêu cầu thiết bị.', risk: 'Vừa', dependencies: ['equipment.requests.view'], scopes: ['Công ty', 'Chi nhánh', 'Dữ liệu của tôi'] },
          { code: 'approve', name: 'Duyệt', description: 'Phê duyệt điều động thiết bị.', risk: 'Cao', dependencies: ['equipment.requests.view'], scopes: ['Công ty', 'Chi nhánh'] },
        ],
      },
    ],
  },
  {
    code: 'projects',
    name: 'Dự án',
    description: 'Quản lý dự án, tiến độ, chi phí và tài liệu.',
    resources: [
      {
        code: 'records',
        name: 'Hồ sơ dự án',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem danh sách và chi tiết dự án.', risk: 'Thấp', scopes: ['Công ty', 'Chi nhánh', 'Dữ liệu của tôi'] },
          { code: 'create', name: 'Tạo', description: 'Tạo dự án mới.', risk: 'Vừa', dependencies: ['projects.records.view'], scopes: ['Công ty'] },
          { code: 'update', name: 'Sửa', description: 'Cập nhật hồ sơ dự án.', risk: 'Cao', dependencies: ['projects.records.view'], scopes: ['Công ty', 'Chi nhánh'] },
          { code: 'delete', name: 'Xóa', description: 'Xóa dự án.', risk: 'Rất cao', dependencies: ['projects.records.view'], scopes: ['Công ty'] },
        ],
      },
      {
        code: 'costs',
        name: 'Chi phí',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem chi phí dự án.', risk: 'Cao', scopes: ['Công ty', 'Chi nhánh'] },
          { code: 'update', name: 'Sửa', description: 'Cập nhật chi phí dự án.', risk: 'Rất cao', dependencies: ['projects.costs.view'], scopes: ['Công ty'] },
          { code: 'approve', name: 'Duyệt', description: 'Duyệt chi phí phát sinh.', risk: 'Rất cao', dependencies: ['projects.costs.view'], scopes: ['Công ty'] },
        ],
      },
    ],
  },
  {
    code: 'reports',
    name: 'Báo cáo',
    description: 'Xem, xuất và chia sẻ báo cáo vận hành.',
    resources: [
      {
        code: 'analytics',
        name: 'Phân tích',
        actions: [
          { code: 'view', name: 'Xem', description: 'Xem báo cáo phân tích.', risk: 'Vừa', scopes: ['Công ty', 'Chi nhánh'] },
          { code: 'export', name: 'Xuất', description: 'Xuất dữ liệu báo cáo.', risk: 'Cao', dependencies: ['reports.analytics.view'], scopes: ['Công ty'] },
          { code: 'share', name: 'Chia sẻ', description: 'Chia sẻ báo cáo cho người khác.', risk: 'Cao', dependencies: ['reports.analytics.view'], scopes: ['Công ty'] },
        ],
      },
    ],
  },
];

export function buildPermissionKey(moduleCode: string, resourceCode: string, actionCode: string) {
  if (resourceCode === 'general') return `${moduleCode}.${actionCode}`;
  return `${moduleCode}.${resourceCode}.${actionCode}`;
}

export function splitPermissionKey(key: string) {
  const normalized = key.includes(':') ? key.replace(':', '.') : key;
  const parts = normalized.split('.').filter(Boolean);
  return {
    moduleCode: parts[0] || '',
    resourceCode: parts.length > 2 ? parts[1] : 'general',
    actionCode: parts.length > 2 ? parts[2] : parts[1] || '',
  };
}

export function flattenPermissionCatalog() {
  return permissionCatalog.flatMap((module) =>
    module.resources.flatMap((resource) =>
      resource.actions.map((action) => ({
        key: buildPermissionKey(module.code, resource.code, action.code),
        module,
        resource,
        action,
      })),
    ),
  );
}

export type FlatPermission = ReturnType<typeof flattenPermissionCatalog>[number];

export function getPermissionMeta(key: string) {
  return flattenPermissionCatalog().find((item) => item.key === key) || null;
}

export function getPermissionLabel(key: string) {
  const meta = getPermissionMeta(key);
  if (!meta) return key;
  return `${meta.module.name} / ${meta.resource.name} / ${meta.action.name}`;
}

export function getDependencyWarnings(permissionKey: string, enabledKeys: string[], nextEnabled: boolean) {
  const current = new Set(enabledKeys);
  const meta = getPermissionMeta(permissionKey);
  if (!meta) return [];

  if (nextEnabled) {
    return (meta.action.dependencies || [])
      .filter((dependency) => !current.has(dependency))
      .map((dependency) => `Nên bật thêm quyền phụ thuộc: ${getPermissionLabel(dependency)}`);
  }

  return flattenPermissionCatalog()
    .filter((item) => current.has(item.key) && (item.action.dependencies || []).includes(permissionKey))
    .map((item) => `Quyền đang phụ thuộc có thể bị ảnh hưởng: ${getPermissionLabel(item.key)}`);
}

export function getConflictWarnings(permissionKey: string, enabledKeys: string[]) {
  const current = new Set(enabledKeys);
  const meta = getPermissionMeta(permissionKey);
  if (!meta?.action.conflicts?.length) return [];

  return meta.action.conflicts
    .filter((conflict) => current.has(conflict))
    .map((conflict) => `Xung đột với quyền: ${getPermissionLabel(conflict)}`);
}

export function normalizePermissionsJson(value: unknown): Record<string, string[]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result: Record<string, string[]> = {};

  Object.entries(value as Record<string, unknown>).forEach(([moduleCode, actions]) => {
    if (!Array.isArray(actions)) return;

    actions.forEach((rawAction) => {
      if (typeof rawAction !== 'string') return;
      const normalized = rawAction.includes('.') || rawAction.includes(':')
        ? splitPermissionKey(rawAction)
        : { moduleCode, resourceCode: 'general', actionCode: rawAction };
      const permissionModule = normalized.moduleCode || moduleCode;
      const actionCode = normalized.resourceCode === 'general'
        ? normalized.actionCode
        : `${normalized.resourceCode}.${normalized.actionCode}`;

      if (!permissionModule || !actionCode) return;
      result[permissionModule] = Array.from(new Set([...(result[permissionModule] || []), actionCode]));
    });
  });

  return result;
}

export function permissionJsonToKeys(value: unknown) {
  const normalized = normalizePermissionsJson(value);
  const catalog = flattenPermissionCatalog();
  
  return Object.entries(normalized).flatMap(([moduleCode, actions]) => {
    return actions.map((action) => {
      // 1. Nếu đã có định dạng module.resource.action, giữ nguyên
      if (action.includes('.')) return `${moduleCode}.${action}`;
      
      // 2. Nếu chỉ có action (legacy), tìm resource phù hợp từ catalog
      const match = catalog.find(item => item.module.code === moduleCode && item.action.code === action);
      if (match) return match.key;
      
      // 3. Fallback mặc định
      return `${moduleCode}.general.${action}`;
    });
  });
}

export function permissionKeysToJson(keys: string[]) {
  const result: Record<string, string[]> = {};

  keys.forEach((key) => {
    const { moduleCode, resourceCode, actionCode } = splitPermissionKey(key);
    if (!moduleCode || !actionCode) return;
    const action = resourceCode === 'general' ? actionCode : `${resourceCode}.${actionCode}`;
    result[moduleCode] = Array.from(new Set([...(result[moduleCode] || []), action]));
  });

  return result;
}
